import React, { useContext, useRef, useState } from 'react';
import { Loading, PageError } from '../../uiComponents/common';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DateFilter, Show } from '../../components';
import { useSelector } from 'react-redux';
import { useCreateBreadCrumbs, useUser } from '../../hooks';
import { hasAllowedRoles } from '../../helpers/roles';
import { useTranslation } from 'react-i18next';
import Loyalty from './components/loyalty';
import KycDoc from './components/kycDoc';
import { WelletContext } from '@/context/wellet/welletContext';
import { defaultQueryOptions } from '@/utils/defaultQueryOptions';
import { useQuery } from 'react-query';
import { getProfilePic, getUserById } from '@/services';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import UsersForm from './usersForm';
import UserHeader from './components/userHeader/userHeader';
import { Button } from '@/components/ui/button';
import UserReservations from './components/userReservations/userReservations';
import { getQueryParam } from '@/helpers/query';
import ProfessionalDocumentation from './components/professionalDocumentation/professionalDocumentation';

const tabsTypes = {
  PROFILE: 'PROFILE',
  LOYALTY: 'LOYALTY',
  DOCUMENTATION: 'DOCUMENTATION',
  DOCUMENTATION_PROFESSIONAL: 'DOCUMENTATION_PROFESSIONAL',
  RESERVATION: 'RESERVATION',
};

const UserProfile = () => {
  const { id } = useParams();
  const { t } = useTranslation('translation', { keyPrefix: 'screens' });
  const welletContext = useContext(WelletContext);
  const organization = useSelector((state) => state.app.selectedOrganization);
  const [profilePicBase64, setProfilePicBase64] = useState(null);
  const formRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const { t: tFormButton } = useTranslation('translation', { keyPrefix: 'components.formButton' });
  const { t: tSidebar } = useTranslation('translation', { keyPrefix: 'components.sideBar' });
  const { t: tGeneric } = useTranslation('translation', { keyPrefix: 'generic' });
  const location = useLocation();
  const tabByUrl = getQueryParam('tab', location);
  const [tab, setTab] = useState(tabByUrl || tabsTypes.PROFILE);
  const navigate = useNavigate();

  const adminUser = useSelector((state) => state.user.user.sid);
  const { roles } = useUser();
  const [kycStatus, setkycStatus] = useState('');
  const isFromInternal = location.pathname.split('/')[1] === 'internalStaff';
  const isFromAffiliate = location.pathname.split('/')[1] === 'affiliates';
  const isFromSeller = location.pathname.split('/')[1] === 'sellers';
  const isFromSellerkYC = location.pathname.split('/')[1] === 'pendingAssociates';
  const isFromUser = location.pathname.split('/')[2] === 'team-and-security';
  const isFromNewUser = location.pathname.split('/')[3] === 'new-user';
  const currentId = isFromNewUser ? false : id ? id : adminUser;

  const userQuery = useQuery({
    queryKey: ['user', currentId, organization.id],
    queryFn: () => getUserById(welletContext, currentId),
    onSuccess: (data) => {
      if (
        data?.data?.organization?.isMarket &&
        data?.data?.organization?.id !== organization.marketId
      ) {
        if (isFromSeller) {
          navigate('/sellers');
        } else if (isFromUser) {
          navigate('/settings/team-and-security');
        } else if (isFromAffiliate) {
          navigate('/affiliates');
        } else if (isFromInternal) {
          navigate('/internalStaff');
        }
      }
      setkycStatus(data?.data?.kycStatus);
    },
    enabled: !!currentId,
    ...defaultQueryOptions,
  });

  const profilePic = useQuery({
    queryKey: ['profile-pic-ambassadors', userQuery?.data?.data?.profilePic, id],
    queryFn: () =>
      getProfilePic(
        welletContext,
        userQuery?.data?.data?.profilePic?.slice(
          userQuery?.data?.data?.profilePic.indexOf('/users'),
        ),
      ),
    onSuccess: async (data) => {
      // Crear un nuevo Blob a partir de los datos recibidos, especificando el tipo MIME como 'image/jpeg'
      const blob = new Blob([data.data], { type: 'image/jpeg' });

      // Función para convertir un Blob a una cadena base64
      const convertBlobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader(); // Crear un nuevo objeto FileReader
          reader.readAsDataURL(blob); // Leer el Blob como una URL de datos (base64)
          reader.onloadend = () => {
            resolve(reader.result); // Resolver la promesa con el resultado en base64 cuando la lectura termine
          };
          reader.onerror = (error) => {
            reject(error); // Rechazar la promesa si ocurre un error durante la lectura
          };
        });
      };

      const base64 = await convertBlobToBase64(blob);
      setProfilePicBase64(base64);
    },
    ...defaultQueryOptions,
    staleTime: 0,
    cacheTime: 0,
    enabled: !!userQuery?.data?.data?.profilePic && !!id,
  });

  const currentCrumbs = isFromInternal
    ? [
        { name: tSidebar('internalStaff'), path: '/internalStaff' },
        { name: t('profile.breadcrumbsConcierges', { concierge: tGeneric('internal_staff') }) },
      ]
    : isFromAffiliate
    ? [
        { name: tSidebar('affiliates'), path: '/affiliates' },
        { name: t('profile.breadcrumbsConcierges', { concierge: tGeneric('affiliate') }) },
      ]
    : isFromSeller
    ? [
        { name: t('concierges.title'), path: '/sellers' },
        { name: t('profile.breadcrumbsConcierges', { concierge: tGeneric('concierge') }) },
      ]
    : isFromUser
    ? [
        { name: t('users.breadcrumbs.prev'), path: '/settings' },
        { name: t('users.breadcrumbs.current'), path: '/settings/team-and-security' },
        {
          name: id ? t('profile.breadcrumbsEditUser') : t('profile.breadcrumbsNewUser'),
          path: id ? `/users/edit-users/${id}` : '/new-user',
        },
      ]
    : isFromSellerkYC
    ? [
        { name: t('profile.breadcrumbsSellersKyc'), path: location.state },
        { name: t('profile.breadcrumbsConcierges', { concierge: tGeneric('concierge') }) },
      ]
    : [
        {
          name: t('profile.breadcrumbs'),
        },
      ];

  const canSeeDocumentation =
    (isFromSeller || isFromSellerkYC || isFromAffiliate || isFromInternal) &&
    hasAllowedRoles(roles, [
      'admin',
      'superadministrator',
      'affiliatecenter',
      'affiliatecenter-onlyview',
      'marketadmin',
      'bosssales',
      'approvekyc',
    ]);

  const onTabChange = (value) => {
    setTab(value);
  };

  useCreateBreadCrumbs(currentCrumbs, location.pathname);

  const handleClickBannerDoc = () => {
    setTab(tabsTypes.DOCUMENTATION);
  };

  const goBackToSetting = () => {
    if (isFromSeller) {
      navigate('/sellers');
    } else if (isFromUser) {
      navigate('/settings/team-and-security');
    } else if (isFromAffiliate) {
      navigate('/affiliates');
    } else if (isFromInternal) {
      navigate('/internalStaff');
    } else {
      navigate('/');
    }
  };

  return (
    <>
      <Show>
        <Show.When isTrue={userQuery?.isLoading || profilePic?.isFetching}>
          <Loading />
        </Show.When>
        <Show.When isTrue={userQuery?.isError}>
          <PageError />
        </Show.When>
        <Show.Else>
          <Tabs value={tab} onValueChange={onTabChange} defaultValue={tab}>
            <UserHeader
              user={userQuery?.data?.data}
              avatar={profilePicBase64}
              setAvatar={setProfilePicBase64}
              loadingAvatar={profilePic?.isFetching}
              uploaderEnabled={tab === tabsTypes.PROFILE}
              isFromSeller={isFromSeller || isFromAffiliate || isFromInternal}
              tabs={
                (isFromSeller || isFromAffiliate || isFromInternal) && (
                  <TabsList className='mb-2'>
                    <TabsTrigger value={tabsTypes.PROFILE}>{t('profile.breadcrumbs')}</TabsTrigger>
                    {canSeeDocumentation && (
                      <TabsTrigger value={tabsTypes.DOCUMENTATION}>
                        {t('profile.documentationKyc.title')}
                        {kycStatus === 'SEND' ? (
                          <div className='tw-ml-2 tw-bg-blue-600 tw-text-white tw-text-xs tw-h-2 tw-w-2 tw-rounded-full'></div>
                        ) : null}
                      </TabsTrigger>
                    )}
                    <TabsTrigger value={tabsTypes.DOCUMENTATION_PROFESSIONAL}>
                      {t('profile.documentationProfessional.title')}
                      {userQuery?.data?.data?.workCredentialStatus === 'SEND' ? (
                        <div className='tw-ml-2 tw-bg-blue-600 tw-text-white tw-text-xs tw-h-2 tw-w-2 tw-rounded-full'></div>
                      ) : null}
                    </TabsTrigger>

                    <TabsTrigger value={tabsTypes.LOYALTY}>{'Loyalty'}</TabsTrigger>
                    <TabsTrigger value={tabsTypes.RESERVATION}>
                      {t('reservations.title')}
                    </TabsTrigger>
                  </TabsList>
                )
              }
              endComponent={
                tab === tabsTypes.PROFILE || tab === tabsTypes.DOCUMENTATION_PROFESSIONAL ? (
                  hasAllowedRoles(roles, [
                    'superadministrator',
                    'marketadmin',
                    'affiliatecenter',
                    'admin',
                  ]) ||
                  isFromNewUser ||
                  isFromUser ? (
                    <div className='row flex-nowrap align-items-center align tw-gap-2'>
                      <Button
                        size={'sm'}
                        variant={'outline'}
                        type='button'
                        onClick={goBackToSetting}
                        disabled={isSaving}
                      >
                        {tFormButton('cancel')}
                      </Button>
                      <Button
                        size={'sm'}
                        type='button'
                        disabled={isSaving}
                        onClick={() => {
                          formRef?.current?.click();
                        }}
                      >
                        {tFormButton('save')}
                      </Button>
                    </div>
                  ) : null
                ) : (
                  tab === tabsTypes.RESERVATION && (
                    <DateFilter isMultipleDate={tab === tabsTypes.RESERVATION} />
                  )
                )
              }
            />
            <TabsContent value={tabsTypes.PROFILE}>
              <UsersForm
                id={currentId}
                userQuery={userQuery}
                profilePic={profilePic}
                setIsSaving={setIsSaving}
                profilePicBase64={profilePicBase64}
                mode={
                  isFromSeller || isFromAffiliate || isFromInternal
                    ? 'CONCIERGE'
                    : isFromUser
                    ? 'USER'
                    : 'PROFILE'
                }
                formRef={formRef}
                showKycPending={
                  kycStatus === 'NONE' || kycStatus === 'PENDING' || kycStatus === 'REVALIDATION'
                }
                showDocumentationBanner={kycStatus === 'SEND'}
                canSeeDocumentation={canSeeDocumentation}
                handleClickBannerDoc={handleClickBannerDoc}
                affiliateType={userQuery?.data?.data?.affiliateType}
              />
            </TabsContent>
            <TabsContent value={tabsTypes.DOCUMENTATION_PROFESSIONAL}>
              <ProfessionalDocumentation
                id={currentId}
                userQuery={userQuery}
                profilePic={profilePic}
                setIsSaving={setIsSaving}
                profilePicBase64={profilePicBase64}
                mode={
                  isFromSeller || isFromAffiliate || isFromInternal
                    ? 'CONCIERGE'
                    : isFromUser
                    ? 'USER'
                    : 'PROFILE'
                }
                formRef={formRef}
                affiliateType={userQuery?.data?.data?.affiliateType}
              />
            </TabsContent>
            <TabsContent value={tabsTypes.DOCUMENTATION}>
              {(isFromSeller || isFromSellerkYC || isFromAffiliate || isFromInternal) &&
                hasAllowedRoles(roles, [
                  'admin',
                  'superadministrator',
                  'affiliatecenter',
                  'affiliatecenter-onlyview',
                  'marketadmin',
                  'bosssales',
                  'approvekyc',
                ]) && (
                  <KycDoc
                    id={id}
                    setkycStatus={setkycStatus}
                    isFromAffiliate={isFromAffiliate}
                    isFromInternal={isFromInternal}
                    affiliateType={userQuery?.data?.data?.affiliateType}
                  />
                )}
            </TabsContent>
            <TabsContent value={tabsTypes.RESERVATION}>
              <UserReservations />
            </TabsContent>
            <TabsContent value={tabsTypes.LOYALTY}>
              <Loyalty
                id={id}
                affiliateType={userQuery?.data?.data?.affiliateType}
                isFromAffiliate={isFromAffiliate}
                isFromInternal={isFromInternal}
                userQuery={userQuery}
              />
            </TabsContent>
          </Tabs>
        </Show.Else>
      </Show>
    </>
  );
};

export default UserProfile;
